import * as Sentry from '@sentry/nextjs';
import { GetServerSidePropsContext } from 'next';
import Head from 'next/head';
import { useEffect } from 'react';

import RenderOnClientReady from '~components/RenderOnClient';
import { DEFAULT_DOCUMENT_TITLE, SERVER_TYPE, WEB_URL } from '~constant/env';
import CompanyPage from '~features/company/CompanyPage';
import { CompanyInfoResponse } from '~hooks/query/company/useCompanyInfo';
import { WantedJobPostingResponse } from '~hooks/query/company/useWantedJobPosting';
import axiosServer from '~lib/api/server';
import { IndustryItem, ISalaryResponse } from '~types/company';
import { getCompanyOgTitle } from '~utils/common/getCompanyOgInfo';
import { setGtmDataLayer } from '~utils/common/thirdPartyEvent';

interface Props {
  title: string;
  description: string;
  url: string;
  companyId: string;
  companyName: string;
  companyCategory: string;
  companyIndustry: string;
  companyJobRecommended: boolean;
}

class ServerSideError extends Error {
  constructor(message: string, ...params: any[]) {
    super(...params);
    this.message = message;
    this.name = 'ServerSideError';
  }
}

function Company({
  title,
  description,
  url,
  companyId,
  companyName,
  companyCategory,
  companyIndustry,
  companyJobRecommended,
}: Props) {
  useEffect(() => {
    setGtmDataLayer('company__view', {
      companyName,
      companyHash: companyId,
      companyType: companyCategory,
      companyIndustry,
      connection: companyJobRecommended ? '추천기업 채용 공고' : '채용 공고',
    });
  }, [companyId]);

  return (
    <>
      <Head>
        <title key='title'>{title}</title>
        <meta key='og-title' property='og:title' content={title} />
        <meta key='og-description' property='og:description' content={description} />
        <meta key='og-url' property='og:url' content={url} />
        <meta
          key='og-image'
          property='og:image'
          content='https://image.wanted.co.kr/kreditjob/www/sns-share-company.jpg'
        />
      </Head>
      <RenderOnClientReady>
        <CompanyPage />
      </RenderOnClientReady>
    </>
  );
}

export async function getServerSideProps(context: GetServerSidePropsContext) {
  let title = '';
  let description = '';
  let url = `${WEB_URL}/company/${context.query.companyId}`;
  let companyName = '';
  let companyCategory = '';
  let companyIndustry = '';
  let companyJobRecommended = false;
  const companyId = context.params?.companyId;

  try {
    const { data: dataIndustries } = await axiosServer.get<IndustryItem[]>('/api/industries');
    const { data: dataCompanyInfo } = await axiosServer.get<CompanyInfoResponse>(
      `/api/company/${companyId}/info`,
    );
    const { data: wantedData } = await axiosServer.get<WantedJobPostingResponse>(
      `https://www.wanted.co.kr/api/v1/company_briefs?kreditjob_pk=${companyId}`,
    );

    const { data: dataSalary } = await axiosServer.get<ISalaryResponse>(
      `/api/company/${companyId}/salaries`,
    );
    title = getCompanyOgTitle(dataCompanyInfo.name, dataSalary) || DEFAULT_DOCUMENT_TITLE;
    description = `${dataCompanyInfo.name}의 상세연봉, 채용공고, 매출, 인원 등 더 다양한 기업 정보를 크레딧잡에서 만나보세요.`;

    companyName = dataCompanyInfo.name;
    companyCategory = dataCompanyInfo.category;
    companyJobRecommended = wantedData.is_recommended;

    if (dataCompanyInfo.industryCode) {
      companyIndustry = dataIndustries.find(
        (industry) => industry.code === dataCompanyInfo.industryCode,
      )?.name as string;
    }
  } catch (e) {
    const error = new ServerSideError('SEO 조회중 에러발생', e);
    Sentry.captureException(error, {
      tags: {
        ssr: true,
      },
      extra: {
        companyId: context.params?.companyId,
        apiUrl: `https://${SERVER_TYPE}.kreditjob.com`,
      },
    });
  }

  return {
    props: {
      title,
      description,
      url,
      companyId,
      companyName,
      companyCategory,
      companyIndustry,
      companyJobRecommended,
    },
  };
}

export default Company;
